import setCookie from "helpers/setCookie";
import type { WhitelabelLocalization } from "@App/whiteLabelReducer";
import {
  SUPPORTED_LANGUAGES,
  addLanguageToPath,
  extractLanguageFromPath,
  isEnglishOnlyPath,
  shouldAddLanguagePrefix,
} from "./languageCore";

/**
 * Partner defaults for a whitelabel site, set in the partner dashboard
 * (Site Configuration > Homepage > Translations) and published as `config.localization`.
 *
 * Language: a visitor who has not picked a language is sent from an unprefixed URL to the
 * partner's default (`/` → `/es/`). Once they pick one in the language selector, that choice is
 * remembered in `WHITELABEL_LANGUAGE_COOKIE` and wins on later visits — including English, which
 * is the unprefixed URL itself.
 *
 * Currency: used whenever the visitor has no `user_currency` cookie of their own.
 */

/** Languages a partner can choose as the default. Mirrors s.base WHITELABEL_DEFAULT_LANGUAGES. */
export const WHITELABEL_DEFAULT_LANGUAGES = ["en", "fr", "es", "de"];

/**
 * The language a visitor picked on this partner's site. Host-only (no `domain`) on purpose: the
 * site-wide `user_language` cookie is shared across `.amberstudent.com` / `.staging-amber.com`, so
 * a choice made on another partner — or on amberstudent.com — must not suppress this partner's
 * default.
 */
export const WHITELABEL_LANGUAGE_COOKIE = "wl_user_language";

type WhitelabelConfigLike = { localization?: WhitelabelLocalization } | null | undefined;

export const getWhitelabelDefaultLanguage = (config: WhitelabelConfigLike): string => {
  const language = config?.localization?.default_language;
  return language && WHITELABEL_DEFAULT_LANGUAGES.includes(language) ? language : "en";
};

/** The partner's default currency code, or "" for "Default" (each property's own currency). */
export const getWhitelabelDefaultCurrency = (config: WhitelabelConfigLike): string => {
  const currency = config?.localization?.default_currency;
  return currency && currency !== "default" ? currency : "";
};

/** A remembered choice is usable only if it is English or has its own URL prefix (not `zh`). */
const isUsableChoice = (language?: string): language is string =>
  !!language &&
  (SUPPORTED_LANGUAGES as readonly string[]).includes(language) &&
  (language === "en" || shouldAddLanguagePrefix(language));

/**
 * Where an unprefixed whitelabel URL should be sent, or `null` to render it as is (English).
 * `search` is the raw query string including its leading `?`, or "".
 */
export const getWhitelabelLanguageRedirect = ({
  path,
  search,
  chosenLanguage,
  defaultLanguage,
}: {
  path: string;
  search: string;
  chosenLanguage?: string;
  defaultLanguage: string;
}): string | null => {
  if (extractLanguageFromPath(path) || isEnglishOnlyPath(path)) return null;

  const target = isUsableChoice(chosenLanguage) ? chosenLanguage : defaultLanguage;
  if (!target || target === "en" || !shouldAddLanguagePrefix(target)) return null;

  return `${addLanguageToPath(path, target)}${search}`;
};

/** Remember the visitor's language pick on this whitelabel site (client only). */
export const saveWhitelabelLanguageChoice = (language: string): void => {
  if (typeof window === "undefined") return;
  setCookie({
    cname: WHITELABEL_LANGUAGE_COOKIE,
    cvalue: language,
    exdays: 365,
    secure: window.location.protocol === "https:",
  });
};
